import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Button } from 'antd';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js';
import SurveyDashboardApi from '../../Api/SurveyDashboardApi';
import ActiveSurveys from '../../Assets/Active_Surveys.png';
import PendingApprovals from '../../Assets/PendingApprovals.png';
import Completion from '../../Assets/Completetion.png';
import ResponseRate from '../../Assets/ResponseRate.png';
import TmMasterTable from '../ReusableComponents/TmMasterTable';
import style from '../../Styles/AddUser.module.css';
import { MONTHS_INDEX } from '../../constant/constant';
ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const SurveyDashboard = ({ month, year }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchDashboard();
  }, [month, year]);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const res = await SurveyDashboardApi.getDashboard({ month, year });
      if (res?.data?.success) {
        setData(res.data.result);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !data) {
    return (
      <div style={{ textAlign: 'center', marginTop: 100 }}>
        {/* <Spin size="large" /> */}
      </div>
    );
  }

  /* ================= GRAPH ================= */

  const graphLabels = Object.keys(data.Graph || {}).map((d) =>
    new Date(d).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    }),
  );

  const graphValues = Object.values(data.Graph || {});

  const chartData = {
    labels: graphLabels,
    datasets: [
      {
        data: graphValues,
        backgroundColor: '#2f80ed',
        borderRadius: 4,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      y: {
        beginAtZero: true,
        grid: { color: '#e5e7eb' },
      },
      x: { grid: { display: false } },
    },
  };

  /* ================= TABLE ================= */

  const tableColumns = [
    { field: 'serialNo', headerName: 'S.NO' },
    {
      field: 'title',
      headerName: 'SURVEY TITLE',
      render: (val, row) => (
        <span
          style={{ cursor: 'pointer', textDecoration: 'none' }}
          onClick={(e) => { 
            e.stopPropagation(); 
            const sId = row.id || row.survey_id;
            navigate(`/survey_report/${btoa(String(sId))}/base_users`); 
          }}
        >
          {val}
        </span>
      )
    },
    // { field: 'periodName',     headerName: 'Period' },
    { field: 'startDate', headerName: 'START DATE' },
    { field: 'endDate', headerName: 'END DATE' },
    // { field: 'questionCount',  headerName: 'Questions' },
    { field: 'completion_status', headerName: 'COMPLETION' },
    { field: 'approval_status', headerName: 'APPROVAL STATUS' },
  ];

  function formatDate(d) {
    if (!d) return '-';
    const date = new Date(d);
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const yyyy = date.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  }

  const tableRows = (data.List || []).map((survey, index) => ({
    ...survey,
    serialNo: index + 1,
    title: survey.title || '-',
    periodName: survey['PeriodModel.Period_Name'] || survey.period_name || '-',
    startDate: formatDate(survey.start_date),
    endDate: formatDate(survey.end_date),
    questionCount: survey.question_count || survey.questionCount || '0',
    completionCount: data.Completion || '0',
    surveyStatus: survey.approval_status || survey.status || '-',
  }));

  return (
    <div className="mt-1 mt-lg-20 w-100 p-0 m-0">
      {/* HEADER */}

      {/* <div className={`mx-3 ${style['add-user']} mb-0`}>Survey Dashboard</div> */}
      {/* TOP CARDS + CHART */}
      <Row gutter={[20, 20]}>
        <Col xs={24} lg={16}>
          <Row gutter={[20, 20]} style={{ height: '85%', marginTop: 5 }}>
            <Col xs={24} sm={12}>
              <Card 
                className="dashboard-card" 
                onClick={() => {
                  const mIndex = MONTHS_INDEX.find(m => m.label === month)?.value ?? moment().month();
                  // Passing 1-indexed month to URL
                  navigate(`/survey?month=${mIndex + 1}&year=${year}`);
                }} 
                style={{ cursor: 'pointer' }}
              >
                <div className="card-wrapper">
                  <div>
                    <div className="card-title">Active Surveys</div>
                    <div className="card-number">{data.Active_Surveys}</div>
                  </div>

                  <div className="icon-box green">
                    <img src={ActiveSurveys} alt="Active Surveys" />
                  </div>
                </div>
              </Card>
            </Col>

            <Col xs={24} sm={12}>
              <Card 
                className="dashboard-card" 
                onClick={() => {
                  const mIndex = MONTHS_INDEX.find(m => m.label === month)?.value ?? moment().month();
                  // Passing 1-indexed month to URL for easier reading, and defaulting to Pending status
                  navigate(`/approvals?month=${mIndex + 1}&year=${year}&status=Pending`);
                }} 
                style={{ cursor: 'pointer' }}
              >
                <div className="card-wrapper">
                  <div>
                    <div className="card-title">Pending Approvals</div>
                    <div className="card-number">{data.Pending_Approals}</div>
                  </div>

                  <div className="icon-box orange">
                    <img src={PendingApprovals} alt="Pending Approvals" />
                  </div>
                </div>
              </Card>
            </Col>

            <Col xs={24} sm={12}>
              <Card 
                className="dashboard-card" 
                onClick={() => {
                  const mIndex = MONTHS_INDEX.find(m => m.label === month)?.value ?? moment().month();
                  navigate(`/survey_report?month=${mIndex + 1}&year=${year}&completion_status=Completed`);
                }} 
                style={{ cursor: 'pointer' }}
              >
                <div className="card-wrapper">
                  <div>
                    <div className="card-title">Completion</div>
                    <div className="card-number">{data.Completion}</div>
                  </div>
                  <div className="icon-box blue">
                    <img
                      src={Completion}
                      alt="Completion"
                      className="header-logo"
                    />
                  </div>
                </div>
              </Card>
            </Col>

            {/* <Col xs={24} sm={12}>
              <Card className="dashboard-card">
                <div className="card-wrapper">
                  <div>
                    <div className="card-title">Response Rate</div>
                    <div className="card-number">{data.Responce_Rate}%</div>
                  </div>
                  <div className="icon-box purple">
                    <img
                      src={ResponseRate}
                      alt="Response Rate"
                      className="header-logo"
                    />
                  </div>
                </div>
              </Card>
            </Col> */}
          </Row>
        </Col>

        <Col xs={24} lg={8}>
          <Card className="dashboard-card" style={{ height: '100%', marginTop: 5 }}>
            <div style={{ fontWeight: 600, marginBottom: 15, }}>
              Survey Completion
            </div>
            <div style={{ height: 260 }}>
              <Bar data={chartData} options={chartOptions} />
            </div>
          </Card>
        </Col>
      </Row>

      {/* TABLE */}
      <Card className="dashboard-card" style={{ marginTop: 20 }} bodyStyle={{ padding: 0 }}>
        {/* <div
          style={{

          background: '#f5f6f8',
            padding: '12px 20px',
            fontWeight: 600,
            borderBottom: '1px solid #e5e7eb',
          }}
        >
          All Surveys
          <Button type="link" style={{ float: 'right', padding: 0 }}>
            View All
          </Button>
        </div> */}

        <div style={{ padding: '20px' }}>
          <TmMasterTable
            buttonPermission={{ add: false, edit: false, delete: false, view: false }}
            rows={tableRows}
            columns={tableColumns}
            custom={[]}
            toggleModal={() => { }}
            handleDelete={() => { }}
            hidePagination={true}
          />
        </div>
      </Card>

      {/* CUSTOM STYLE */}
      <style>{`
        .dashboard-card {
          border-radius: 0px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.05);
        }
        .card-title {
          color: #6b7280;
          font-size: 14px;
          margin-bottom: 8px;
        }
        .card-number {
          font-size: 28px;
          font-weight: 700;
        }
        .ant-table-thead > tr > th {
          background: #f0f2f5 !important;
          font-weight: 600;
        }
        .dashboard-card {
          border-radius: 2px;
        }

        .card-wrapper {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .card-title {
          font-size: 14px;
          color: #8c8c8c;
          margin-bottom: 6px;
        }

        .card-number {
          font-size: 28px;
          font-weight: 700;
        }

        .icon-box {
          width: 48px;
          height: 48px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .icon-box img {
          width: 33px;
        }
      `}</style>
    </div>
  );
};

export default SurveyDashboard;